import { useState, useRef, useEffect } from 'react';

import { SSLayout } from '@/src/layout/superstack/SSLayout';
import styles from '@/styles/superstack/stacks/companies.module.scss';
import classNames from 'classnames/bind';
import { Button } from '@/src/component/superstack/base/Button';
import { CardCompanyStacks } from '@/src/component/superstack/companies/CardCompanyStacks';
import useSWRImmutable from 'swr/immutable';
import { api, BASE_API_URL } from '@/src/utils/api';
import { ApiUrls } from '@/data/superstack/ApiUrls';
import { InputSearchGroup } from '@/src/component/superstack/base/InputSearchGroup';
import { Input } from '@/src/component/superstack/base/Inputs';
import { SSPagination } from '@/src/component/superstack/base/Pagination';
import { CompanyStacksCTA } from '@/src/component/superstack/companies/CompanyBanners';
import FadeLoader from 'react-spinners/FadeLoader';
import { useRouter } from 'next/router';
import useSSProfile from '@/src/utils/hooks/superstack/useSSProfile';
import useUserCompany from '@/src/utils/hooks/superstack/useUserCompany';

const PAGINATED_BY = 12;

const fetcher = (url) => {
  return api.get(url).then((result) => result.data);
};

export default function StacksCompaniesWrapper() {
  const router = useRouter();
  const {
    keyword = '',
    stack = '',
    page = 1,
  } = {
    ...router.query,
  };
  const [keyword_, setKeyword_] = useState(keyword);
  const [stackFlag, setStackFlag] = useState(stack);
  const [page_, setPage_] = useState(page || 1);

  useEffect(() => {
    setKeyword_(keyword);
    setStackFlag(stack);
    setPage_(page);
  }, [keyword, stack, page]);

  useEffect(() => {
    const urlObj = {
      pathname: router.pathname,
      query: {
        ...router.query,
        keyword: keyword_,
        stack: stackFlag,
        page: page_,
      },
    };
    const qs = Object.entries(urlObj.query)
      .map(([key, val]) => {
        return `${key}=${val}`;
      })
      .join('&');
    const url = `${urlObj.pathname}/?${qs}`;
    window.history.replaceState({ ...window.history.state, as: url, url: url }, '', url);
  }, [keyword_, stackFlag, page_, router.pathname, router.query]);

  const { data, error } = useSWRImmutable(
    [
      BASE_API_URL +
        ApiUrls().find((url) => url.name === 'companies_stacks')?.url +
        `?keyword=${keyword_}&stack=${stackFlag}&page=${page_}`,
    ],
    fetcher,
    { shouldRetryOnError: false },
  );

  const targets = data?.companies || [];
  const isLoading = !error && !data;

  useEffect(() => {
    if (!isLoading && targets.length === 0 && (page > 1 || page_ > 1)) setPage_(1);
  }, [page, targets.length, isLoading]);

  return (
    <StacksCompanies
      companies={targets}
      error={error}
      keyword_={keyword_}
      setKeyword_={setKeyword_}
      setStackFlag={setStackFlag}
      page_={page_}
      setPage_={setPage_}
      total_companies_cnt={data?.total_companies_count || 0}
      isLoading={isLoading}
    />
  );
}

const StacksCompanies = ({
  companies,
  keyword_,
  setKeyword_,
  setStackFlag,
  page_,
  setPage_,
  total_companies_cnt,
  isLoading,
}) => {
  const { user: { data: user = {} } = {}, isLoading: isUserLoading } = useSSProfile();
  const { data: { data: company = {} } = {}, isLoading: isCompanyLoading } = useUserCompany(
    user.username,
  );
  const cx = classNames.bind(styles);
  const keywordRef = useRef();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setKeyword_(keywordRef.current.value);
      setStackFlag('');
    }
  };
  const handleSearch = () => {
    setKeyword_(keywordRef.current.value);
    setStackFlag('');
  };

  const handlePageChange = (e, page) => {
    if (!page) return;
    setPage_(page);
    window.scrollTo(0, 0);
  };
  const hasCompanyStack = company.stacks && company.stacks.length > 0;

  return (
    <SSLayout
      pageTitle={'기술 스택'}
      width={'100%'}
      sx={{ position: 'relative', backgroundColor: '#FAFAFA' }}
    >
      {!isUserLoading &&
        !isCompanyLoading &&
        (user?.username ? (
          hasCompanyStack ? null : (
            <CompanyStacksCTA bannerMT={'-170px'} bannerHeight={'320px'} />
          )
        ) : null)}
      <section
        className={cx('body')}
        style={user?.username && !hasCompanyStack ? { marginTop: '24px' } : { paddingTop: '24px' }}
      >
        <div className={cx('header')} style={{ marginBottom: '16px' }}>
          <p className={cx('text')}>기술 스택</p>
          <div className={cx('search-box-wrapper')}>
            <div className={cx('search-box')}>
              <InputSearchGroup size={'sm'}>
                <Input
                  size={'sm'}
                  placeholder={'기업명, 기술스택 입력'}
                  defaultValue={keyword_}
                  handleKeyPress={handleKeyPress}
                  ref={keywordRef}
                />
              </InputSearchGroup>
            </div>
            <Button mode={'filled'} size={'lg'} onClick={handleSearch}>
              검색
            </Button>
          </div>
        </div>
        <section className={cx('companies-wrapper')}>
          {companies?.map((company) => {
            return <CardCompanyStacks key={company.id} company={company} />;
          })}
        </section>
        <FadeLoader
          color={'gray'}
          loading={isLoading}
          cssOverride={{
            top: '200px',
            margin: '0 auto',
          }}
        />
        {!isLoading && total_companies_cnt === 0 && (
          <article className={cx('empty-result-box')}>
            <p className={cx('title')}>결과가 없습니다.</p>
            <ul style={{ listStyleType: 'disc', marginTop: '20px' }}>
              <li>필터를 다시 설정하거나, 필터 개수를 줄여보세요.</li>
              <li>단어의 철자가 정확한지 확인해 보세요.</li>
              <li>한글을 영어로 혹은 영어를 한글로 입력했는지 확인해 보세요.</li>
              <li>검색어의 단어 수를 줄이거나, 보다 일반적인 검색어로 다시 검색해 보세요.</li>
              <li>두 단어 이상의 검색어인 경우, 띄어쓰기를 확인해 보세요.</li>
            </ul>
          </article>
        )}
        {total_companies_cnt > 0 && (
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <SSPagination
              countItems={total_companies_cnt}
              paginatiedBy={PAGINATED_BY}
              page={page_}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
      </section>
    </SSLayout>
  );
};
